import { useEffect, useCallback, useState } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import Error from "./common/Error/Error";
import CustomerRoutes from "./customers/CustomerRoutes";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { RootState } from "./store/store";
import { deleteCookie, getCookie } from "./utils/Utility";
import Registration from "./registration/Registration";
import { setUserDetails } from "./Login/userAuthSlice";
import AdminRoutes from "./admin/AdminRoutes";
import { setAdminDetails } from "./admin/Login/adminAuthSlice";
import { adminPages, agentPages, customerPages } from "./constants/Constants";
import { getFeatures } from "./admin/Features/featuresActions";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { Box } from "@mui/material";
import { createAppTheme } from "./app-theme/theme";
import { getUiSettings } from "./admin/UiSettings/uiSettingsActions";
import AgentsRoutes from "./Agents/AgentRoutes";
import { setAgentDetails } from "./Agents/Login/agentAuthSlice";
import App from "./App";
import CustomerLogin from "./customers/Login/CustomerLogin";

const ApplicationRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const authenticated = getCookie("authenticated");
  const id = getCookie("id");
  const name = getCookie("name");
  const roleCode = getCookie("roleCode");
  const carrier = getCookie("carrier");
  const system = getCookie("system");
  const emailInfo = getCookie("emailInfo");
  const firstName = getCookie("firstName");
  const lastName = getCookie("lastName");
  const providerId = getCookie("providerId");
  const status = getCookie("status");
  const typeCd = getCookie("typeCs");
  const userId = getCookie("userId");
  const providerCd = getCookie("provCd"); //"test";
  const providerTypeCd = getCookie("producerTypeCd"); //"Producer";
  const isPasswordExpired = !!(getCookie("isPasswordExpired") === "true");
  const { uiSettings } = useAppSelector((state: RootState) => state.uiSettings);

  const { isLoading, calledReducerType } = useAppSelector(
    (state: RootState) => state.common
  );
  const { userDetails } = useAppSelector((state: RootState) => state.userAuth);
  const { adminDetails } = useAppSelector(
    (state: RootState) => state.adminAuth
  );
  const { agentDetails } = useAppSelector(
    (state: RootState) => state.agentAuth
  );
  const [loaderURL, setLoaderURL] = useState<string | File | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const routeToAgent = useCallback(() => {
    const pathArray = location.pathname.split("/");
    const searchParams = location?.search;

    // If on login page, navigate to mytasks
    if (pathArray[1] === "agent" && pathArray[2] === "login") {
      navigate("/agent/mytasks");
      return;
    }

    // If already on agent route and not on login/register, don't redirect
    if (
      pathArray[1] === "agent" &&
      pathArray[2] !== "login" &&
      pathArray[2] !== "register"
    ) {
      return;
    }

    // If on a valid agent page, stay on that page
    if (
      pathArray[1] === "agent" &&
      agentPages.some((page: string) => page === pathArray[2])
    ) {
      searchParams.trim()
        ? navigate(`${location.pathname}${searchParams}`)
        : navigate(location.pathname);
      return;
    }

    // Default navigation to mytasks
    navigate("/agent/mytasks");
  }, [navigate, location.pathname, location.search]);

  const routeToAdmin = useCallback(() => {
    const pathArray = location.pathname.split("/");
    if (pathArray[1] !== "admin") {
      navigate("/admin/dashboard");
    } else {
      if (
        pathArray[2] &&
        adminPages
          .filter((pageName) => pageName !== "login")
          .some((page: string) => page === pathArray[2])
      ) {
        navigate(location.pathname); // redirect to that page name
      } else {
        const isPasswordExpired = getCookie("isPasswordExpired") === "true";
        if (isPasswordExpired) {
          setOpenDialog(true);
        } else {
          navigate("/admin/dashboard");
        }
      }
    }
  }, [navigate, location.pathname]);

  const routeToCustomer = useCallback(() => {
    const pathArray = location.pathname.split("/");
    const defaultCustomerRoute =
      id === "0987" ? "/customer/newcustomerDashboard" : "/customer/dashboard";

    if (pathArray[1] !== "customer") {
      navigate(defaultCustomerRoute);
    } else {
      if (
        pathArray[2] &&
        customerPages.some((page: string) => page === pathArray[2])
      ) {
        navigate(location.pathname);
      } else {
        navigate(defaultCustomerRoute);
      }
    }
  }, [navigate, location.pathname, id]);

  const checkAuthentication = useCallback(() => {
    switch (roleCode) {
      case "1":
        if (!adminDetails) {
          dispatch(setAdminDetails({ id, name, isPasswordExpired, roleCode }));
        }
        routeToAdmin();
        return;
      case "2":
        if (!userDetails) {
          dispatch(
            setUserDetails({
              customerId: id,
              customerName: name,
              carrier,
              system,
            })
          );
        }
        routeToCustomer();
        return;
      case "Producer": {
        if (!agentDetails) {
          dispatch(
            setAgentDetails({
              carrier,
              system,
              emailInfo,
              firstName,
              lastName,
              providerId,
              providerCd: providerCd,
              producerTypeCd: providerTypeCd,
              status,
              typeCd,
              userId,
            })
          );
        }
        routeToAgent();
        return;
      }
    }
  }, [
    roleCode,
    routeToAdmin,
    routeToCustomer,
    dispatch,
    id,
    name,
    userDetails,
    adminDetails,
    carrier,
    system,
    agentDetails,
    emailInfo,
    firstName,
    lastName,
    providerId,
    routeToAgent,
    status,
    typeCd,
    userId,
  ]);

  useEffect(() => {
    if (authenticated) {
      checkAuthentication();
    }
  }, [authenticated, checkAuthentication]);

  useEffect(() => {
    dispatch(getUiSettings());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getFeatures());
  }, [dispatch]);

  useEffect(() => {
    if (uiSettings && uiSettings?.loader && uiSettings?.loader !== undefined) {
      setLoaderURL(uiSettings?.loader);
    }
  }, [dispatch, uiSettings]);

  useEffect(() => {
    if (!authenticated) {
      localStorage.removeItem("tableRowsPerPage");
    }
  }, [authenticated]);

  const handleResetPassword = () => {
    setOpenDialog(false);
    navigate("/admin/reset-password");
  };
  return (
    <>
      <ThemeProvider theme={createAppTheme(uiSettings)}>
        <Dialog
          open={
            isLoading &&
            calledReducerType !== "dashboard/getInitBasePrompt" &&
            calledReducerType !== "cms/getContent"
          }
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              backgroundColor: "transparent",
              boxShadow: "none",
              overflow: "hidden",
              position: "relative",
            },
          }}
        >
          {loaderURL && typeof loaderURL === "string" && loaderURL?.length ? (
            <img
              src={`data:image/png;base64,${loaderURL}`}
              alt=""
              height={100}
              width={150}
              className="blinking"
              style={{ objectFit: "contain" }}
            />
          ) : (
            <CircularProgress size="4rem" />
          )}
        </Dialog>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100vh",
          }}
        >
          <Box
            sx={{
              // background:
              //   "linear-gradient(rgb(251, 253, 252) 0%, rgb(229, 246, 254) 100%)",
              flex: 1,
            }}
          >
            <Routes>
              <Route path="/" element={<App />} />
              <Route path="/login" element={<CustomerLogin />} />
              <Route path="/customer/*" element={<CustomerRoutes />} />
              <Route path="/admin/*" element={<AdminRoutes />} />
              <Route path="/agent/*" element={<AgentsRoutes />} />
              <Route path="/registration" element={<Registration />} />
              <Route path="*" element={<Error />} />
            </Routes>
          </Box>
        </Box>
      </ThemeProvider>
      <Dialog
        open={!!authenticated && openDialog}
        aria-labelledby="password-expired-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontSize: "24px",
            fontWeight: 500,
            color: "#333",
          }}
        >
          Password Expired
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              color: "#666",
              fontSize: "16px",
              my: 2,
            }}
          >
            Your password has expired. Please click "Reset Password" to
            continue.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={handleResetPassword}
            sx={{
              bgcolor: "#00BFA5",
              color: "white",
              "&:hover": {
                bgcolor: "#00A693",
              },
              px: 3,
              py: 1,
            }}
          >
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApplicationRoutes;
